import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { AppDispatch, RootState } from "../store";
import { fetchAssetGroups } from "../store/assetGroupSlice";
import { fetchAssetsByGroupId } from "../store/assetsSlice";

// A searchable entity: an asset (default, kind omitted for back-compat with
// persisted schedule mention JSON) or an asset group (no lat/lng; imageUrl is
// the group's profilePictureUrl).
export interface MentionedAsset {
  kind?: "asset" | "group";
  id: string;
  groupId?: string;
  name: string;
  lat?: number;
  lng?: number;
  description?: string;
  imageUrl?: string;
  groupName?: string;
  /** Present on assets (geoPointsByAssetGroupIdCustom selects it); absent on groups */
  processedModels?: { items?: ({ id: string } | null)[] | null } | null;
}

// Shared match predicate so every asset search in the app (the chat's @-mention picker, the
// email schedule's asset field) and the live-mention check can't diverge. An empty query matches
// every asset (`"".includes("")` is true) — intentional, so a bare "@" opens the picker with the
// full list; callers that want "nothing until you type" guard the empty case themselves.
export function assetMatchesQuery(asset: MentionedAsset, q: string): boolean {
  const needle = q.toLowerCase();
  return (
    asset.name.toLowerCase().includes(needle) ||
    (asset.groupName?.toLowerCase().includes(needle) ?? false)
  );
}

/** The entities matching a query, capped so a short query can't paint hundreds of rows. */
export function filterMentionableEntities(
  entities: MentionedAsset[],
  query: string,
  limit = 40
): MentionedAsset[] {
  return entities.filter((entity) => assetMatchesQuery(entity, query)).slice(0, limit);
}

export interface MentionableAssets {
  /** Every asset the user can see, each carrying its group's name and id */
  allAssets: MentionedAsset[];
  allGroups: MentionedAsset[];
  /** Groups first, then assets — the order the chat picker lists them in */
  allEntities: MentionedAsset[];
  /** True until the group list and every group's assets have landed in Redux */
  isLoading: boolean;
}

/**
 * Every asset and group the signed-in user can see, with images, loaded into Redux once and
 * shared by every asset search (the AI Analyst's @-mention picker, the email schedule's asset
 * field). Pulled out of useAtMention so a plain search box can use the same data and matching.
 */
export const useMentionableAssets = (): MentionableAssets => {
  const dispatch = useDispatch<AppDispatch>();

  const dataGroups = useSelector(
    (state: RootState) => state.assets.dataGroups as Record<string, MentionedAsset[]>
  );
  const assetGroups = useSelector(
    (state: RootState) =>
      state.assetGroups.groups as Record<
        string,
        { id: string; name: string; description?: string; profilePictureUrl?: string }
      >
  );
  const groupsLoading = useSelector((state: RootState) => (state.assetGroups as { loading?: string }).loading);

  // The group list may not be loaded yet (e.g. deep link straight to the AI Analyst
  // page — other pages normally dispatch fetchAssetGroups). Fetch it once so the
  // picker has groups and their assets.
  const requestedGroupListRef = useRef(false);
  useEffect(() => {
    if (requestedGroupListRef.current || Object.keys(assetGroups).length > 0) return;
    requestedGroupListRef.current = true;
    dispatch(fetchAssetGroups());
  }, [assetGroups, dispatch]);

  // Tracks groups with an in-flight (or already-attempted) fetch so we never
  // dispatch the same group twice. Without this, the effect re-runs on every
  // dataGroups change and re-dispatches every still-pending group, producing an
  // O(N²) request storm that trips Chrome's net::ERR_INSUFFICIENT_RESOURCES.
  const requestedGroupsRef = useRef<Set<string>>(new Set());

  // Read via a ref so the effect below doesn't re-run N times as each group's
  // assets land in the store; it only needs the snapshot at dispatch time to
  // skip groups some other component already loaded.
  const dataGroupsRef = useRef(dataGroups);
  dataGroupsRef.current = dataGroups;

  // Fetch assets on mount so they're ready before the user types
  useEffect(() => {
    Object.keys(assetGroups).forEach((groupId) => {
      if (dataGroupsRef.current[groupId] || requestedGroupsRef.current.has(groupId)) return;

      requestedGroupsRef.current.add(groupId);
      dispatch(fetchAssetsByGroupId(groupId))
        .unwrap()
        .catch(() => {
          // Allow a retry the next time the group list changes.
          requestedGroupsRef.current.delete(groupId);
        });
    });
  }, [assetGroups, dispatch]);

  const allAssets = useMemo<MentionedAsset[]>(
    () =>
      Object.entries(dataGroups).flatMap(([groupId, assets]) => {
        const groupName: string = assetGroups[groupId]?.name ?? "";
        return assets.map((asset) => ({ ...asset, groupName, groupId }));
      }),
    [dataGroups, assetGroups]
  );

  const allGroups = useMemo<MentionedAsset[]>(
    () =>
      Object.values(assetGroups).map((group) => ({
        kind: "group" as const,
        id: group.id,
        name: group.name,
        description: group.description,
        imageUrl: group.profilePictureUrl,
      })),
    [assetGroups]
  );

  // Groups first, then assets — the picker shows groups at the top of the list.
  const allEntities = useMemo<MentionedAsset[]>(() => [...allGroups, ...allAssets], [allGroups, allAssets]);

  const isLoading = useMemo(() => {
    if (groupsLoading !== "success") return true;
    return Object.keys(assetGroups).some((groupId) => !dataGroups[groupId]);
  }, [groupsLoading, assetGroups, dataGroups]);

  return { allAssets, allGroups, allEntities, isLoading };
};

export default useMentionableAssets;
